import { Checkbox, Stack } from '@mui/material';
import { ParentSearchResult, StaffSearchResult, StatusUserRelation } from '@schooly/api';
import { CreateParentForm } from '@schooly/components/applications';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { CombinedRow } from '@schooly/components/filters';
import { GridCell, GridCellContent, TypographyWithOverflowHint } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Table } from '../../../../components/uikit-components/Table/Table';
import { TableCell } from '../../../../components/uikit-components/Table/Table';
import { LEFT_COL_WIDTH, RIGHT_COL_WIDTH } from './SelectParentContent';

interface StaffListProps {
  entries: CombinedRow<StaffSearchResult>[];
  addAdultToSelected: (adult: ParentSearchResult | StatusUserRelation) => void;
  selectedAdultsIds: string[];
  setSelectedAdults: (value: React.SetStateAction<CreateParentForm[]>) => void;
}

export const StaffList: FC<StaffListProps> = ({
  entries,
  addAdultToSelected,
  selectedAdultsIds,
  setSelectedAdults,
}) => {
  const columns: TableCell[] = useMemo(
    () => [
      {
        id: 'name',
        renderContent: () => (
          <GridCell
            sticky
            noHorizontalPadding
            sx={{
              position: 'relative',
            }}
            pt={0}
            pb={1}
          >
            <GridCellContent>
              <FormattedMessage id="people-Name" />
            </GridCellContent>
          </GridCell>
        ),
      },
      {
        id: 'checkbox',
        renderContent: () => (
          <GridCell
            sticky
            noHorizontalPadding
            pt={0}
            pb={1}
            sx={{ width: RIGHT_COL_WIDTH, position: 'relative' }}
          />
        ),
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    return entries.map((staff) => {
      const user = staff.item.registration.school_user_relation;

      const cellArray: TableCell[] = [
        {
          id: 'name',
          renderContent: () => {
            const name = getUserFullName(user);
            return (
              <GridCell
                sx={{
                  position: 'relative',
                  maxWidth: LEFT_COL_WIDTH,
                }}
                pl={6}
              >
                <AvatarAuth
                  withAvatarPreview
                  user={user}
                  sx={(theme) => ({
                    position: 'absolute',
                    left: theme.spacing(1),
                    marginTop: theme.spacing(-0.5),
                  })}
                />

                <TypographyWithOverflowHint variant="h3" color="common.grey2" noWrap>
                  {name}
                </TypographyWithOverflowHint>
              </GridCell>
            );
          },
        },
        {
          id: 'checkbox',
          renderContent: () => (
            <GridCell sx={{ justifyContent: 'center', position: 'relative' }} noVerticalPadding>
              <Stack alignItems={'center'}>
                <Checkbox
                  checked={selectedAdultsIds.includes(user.user_id)}
                  sx={{
                    visibility: 'hidden',
                    '&.Mui-checked': {
                      visibility: 'visible',
                    },
                  }}
                  onChange={() => {
                    if (!selectedAdultsIds.includes(user.user_id)) {
                      addAdultToSelected(user);
                    } else {
                      setSelectedAdults((prev) =>
                        prev.filter((p) => p.profile_id !== user.user_id),
                      );
                    }
                  }}
                />
              </Stack>
            </GridCell>
          ),
        },
      ];
      return {
        id: user.relation_id,
        cells: cellArray,
      };
    });
  }, [addAdultToSelected, entries, selectedAdultsIds, setSelectedAdults]);

  return <Table rows={rows} columns={columns} withDefaultHover />;
};
